import React, { type ReactNode, useContext } from 'react';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';
import { StickyContainer } from 'react-sticky';

import * as BackstageConstants from '../../constants';
import * as D from '../../deps';

import BackstageLargeHeaderLayout from './BackstageLargeHeaderLayout';

interface ILayoutManagerStateProps {
  // Looks like these will be removed once the isCuratorExperienceEnabled
  // feature flag is removed
  heroImageUrl?: string;
  name?: string;
  shareableUrlPath?: string;
}

interface ILayoutManagerDispatchProps {
  playResumeSource: typeof D.playResumeSource;
}

interface ILayoutManagerOwnProps {
  bio?: ReactNode;
  hasCuratorPrograms?: boolean;
  heroImageUrl?: string;
  isHeaderInSidebar?: boolean;
  isMobileDevice?: boolean;
  largeHeader?: ReactNode;
  mainContent?: ReactNode;
  name?: string;
  pandoraId?: string;
  shareableUrlPath?: string | $TSFixMe;
  sidebarButtons?: ReactNode;
  subTitleLrg?: ReactNode;
  subTitleSm?: ReactNode;
}

type ILayoutManagerProps = ILayoutManagerStateProps &
  ILayoutManagerDispatchProps &
  ILayoutManagerOwnProps;

/*
  Determines layout type to show based on what UI component is passed.
  Also gathers common props and handles common actions
*/
export const LayoutManager: React.FC<ILayoutManagerProps> = props => {
  const { integration } = useContext(D.AppContext);

  const {
    pandoraId,
    name,
    bio,
    shareableUrlPath,
    isMobileDevice,
    playResumeSource,
    heroImageUrl,
    mainContent,
    sidebarButtons,
    subTitleLrg,
    subTitleSm,
    largeHeader,
    isHeaderInSidebar,
    hasCuratorPrograms,
  } = props;

  const headerProps = {
    disableHover: isMobileDevice,
    isHeaderInSidebar,
    subTitleLrg,
    subTitleSm,
    title: name,
    titleUrl: shareableUrlPath,
  };

  const mobilePlayButtonOnClick = (): void => {
    // @ts-expect-error
    // TODO: WEBCA-2934 Fix playResumeSource references
    playResumeSource({
      id: pandoraId,
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      pageUrl: shareableUrlPath,
    });

    integration.action.clicked({
      pandoraId,
      contentSection: BackstageConstants.BackstageSections.CONTROL_BAR,
      componentEventType: BackstageConstants.BackstageEvents.play,
    });
  };

  if (largeHeader) {
    const renderSidebarButtons = (): ReactNode => sidebarButtons;
    const renderMainContent = (): ReactNode => mainContent;
    return (
      <StickyContainer>
        <BackstageLargeHeaderLayout
          // @ts-expect-error TS(2322) FIXME: Type '{ headerBackstage: Element; heroImageUrl: an... Remove this comment to see the full error message
          headerBackstage={<D.HeaderBackstageWithLogger {...headerProps} />}
          heroImageUrl={heroImageUrl}
          heroImageLinkUrl={shareableUrlPath}
          heroImageIsCircle={true}
          renderSidebarButtons={renderSidebarButtons}
          renderMainContent={renderMainContent}
          entityIdentifier={pandoraId}
          renderArtworkPlayButton={isMobileDevice}
          playOnClick={mobilePlayButtonOnClick}
          artistBio={bio}
          largeHeader={largeHeader}
        />
      </StickyContainer>
    );
  }

  // Default Backstage Layout
  return (
    // WEBCA-2963 porting BackstageLayout2 to typescript should fix these errors
    // (also move it to backstage domain)
    <D.BackstageLayout2
      entityIdentifier={pandoraId}
      // @ts-expect-error TS(2322) FIXME: Type '{ disableHover: boolean | undefined; isHeader... Remove this comment to see the full error message
      headerProps={headerProps}
      heroImageUrl={heroImageUrl}
      heroImageIsCircle={true}
      renderArtworkPlayButton={isMobileDevice}
      playOnClick={mobilePlayButtonOnClick}
      // @ts-expect-error
      sidebarButtons={sidebarButtons}
      // @ts-expect-error
      mainContent={mainContent}
      isHeaderInSidebar={isHeaderInSidebar}
      hasCuratorPrograms={hasCuratorPrograms}
    />
  );
};

export const mapStateToProps: MapStateToProps<ILayoutManagerStateProps, ILayoutManagerOwnProps> = (
  state: $TSFixMe,
  ownProps: ILayoutManagerOwnProps,
) => {
  const isCuratorExperienceEnabled = D.Utils.TunerVars.curatorExperienceEnabled(state);
  if (isCuratorExperienceEnabled) {
    return {};
  }
  // Should the below be removed?
  const { pandoraId } = ownProps;
  const annotation = pandoraId
    ? D.getAnnotationsById(state, pandoraId) || BackstageConstants.emptyMap
    : BackstageConstants.emptyMap;

  const heroImageUrl = D.getArt(annotation, 300, state);

  return {
    name: annotation.get('name', '') as string,
    shareableUrlPath: annotation.get('shareableUrlPath'),
    heroImageUrl,
  };
};

const mapDispatchToProps: MapDispatchToProps<ILayoutManagerDispatchProps, ILayoutManagerOwnProps> =
  {
    playResumeSource: D.playResumeSource,
  };

export default connect(mapStateToProps, mapDispatchToProps)(LayoutManager);
